import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Flag, Check, X, Trash2, ShieldAlert, Loader2, MessageSquare,
} from 'lucide-react';
import * as api from '../lib/api';

const REASON_LABELS = {
  inappropriate: 'Inappropriate content',
  spam: 'Spam',
  hate_speech: 'Hate speech',
  other: 'Other',
};

const DURATIONS = [
  { value: '3d', label: '3 days' },
  { value: '7d', label: '7 days' },
  { value: '10d', label: '10 days' },
  { value: '30d', label: '30 days' },
  { value: 'permanent', label: 'Permanent' },
];

const ReportsQueue = ({ users = [], onUserSuspended }) => {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [suspendModal, setSuspendModal] = useState(null);  // { userId, displayName }
  const [duration, setDuration] = useState('7d');
  const [suspendReason, setSuspendReason] = useState('');
  const [crossGender, setCrossGender] = useState(false);
  const [crossLoading, setCrossLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listReports(filter);
      setReports(r.data || []);
    } catch { setReports([]); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.getCrossGenderSetting().then(r => setCrossGender(!!r.data?.enabled)).catch(() => {});
  }, []);

  const toggleCross = async () => {
    setCrossLoading(true);
    const next = !crossGender;
    try {
      await api.setAdminSetting('cross_gender_messaging_enabled', next);
      setCrossGender(next);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to update');
    }
    setCrossLoading(false);
  };

  const handleDismiss = async (id) => {
    setActionId(id);
    try { await api.dismissReport(id); await load(); }
    catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
    setActionId(null);
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('Delete this post permanently? This cannot be undone.')) return;
    setActionId(id);
    try { await api.reportsDeletePost(id); await load(); }
    catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
    setActionId(null);
  };

  const openSuspend = (userId, displayName) => {
    setSuspendModal({ userId, displayName });
    setDuration('7d');
    setSuspendReason('');
  };

  const handleSuspend = async () => {
    if (!suspendModal) return;
    setActionId(suspendModal.userId);
    try {
      const res = await api.suspendUser(suspendModal.userId, { duration, reason: suspendReason || null });
      onUserSuspended?.(suspendModal.userId, res.data?.until);
      setSuspendModal(null);
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to suspend');
    }
    setActionId(null);
  };

  return (
    <div className="space-y-4" data-testid="reports-queue">
      {/* Cross-gender messaging toggle */}
      <div className="flex items-center justify-between gap-4 p-4 rounded-arch-lg border border-primary/15 bg-white/40 dark:bg-white/5">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-5 h-5 text-primary" />
          <div>
            <p className="text-sm font-medium text-text-main">Cross-gender messaging</p>
            <p className="text-xs text-text-muted">When ON, regular Sirati+ members can message across genders. Admins/mods are always reachable.</p>
          </div>
        </div>
        <button onClick={toggleCross} disabled={crossLoading}
          className={`px-4 py-2 rounded-full text-xs font-medium ${crossGender ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'} disabled:opacity-50`}
          data-testid="cross-gender-toggle">
          {crossLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (crossGender ? 'ON' : 'OFF')}
        </button>
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-2 flex-wrap">
        {['pending', 'reviewed', 'dismissed'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${filter === s ? 'bg-primary text-white' : 'bg-white/70 dark:bg-white/10 text-text-muted hover:bg-primary/10'}`}
            data-testid={`reports-filter-${s}`}>
            {s}
          </button>
        ))}
      </div>

      {/* Reports list */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : reports.length === 0 ? (
        <div className="text-center text-text-muted py-10 border border-dashed border-primary/15 rounded-arch">
          <Flag className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No {filter} reports.
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map(r => {
            const post = r.post || {};
            const author = post.author || {};
            const reporter = r.reporter || {};
            return (
              <div key={r.id} className="rounded-arch-lg p-4 border border-primary/15 bg-white/40 dark:bg-white/5"
                data-testid={`report-${r.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Flag className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-text-muted">
                      Reported by <span className="font-medium text-text-main">@{reporter.display_name || 'unknown'}</span> ·
                      {' '}<span className="text-red-600 font-medium">{REASON_LABELS[r.reason] || r.reason}</span> ·
                      {' '}{new Date(r.created_at).toLocaleString()}
                    </p>
                    {r.details && <p className="text-xs text-text-muted italic mt-1">"{r.details}"</p>}
                    <div className="mt-2 p-3 rounded-arch bg-white/70 dark:bg-white/10 border border-primary/10">
                      <p className="text-[11px] text-text-muted mb-1">Post by @{author.display_name || 'deleted'}</p>
                      <p className="text-sm text-text-main whitespace-pre-wrap">{post.content || '— post deleted —'}</p>
                      {post.image_url && <img src={post.image_url} alt="" className="mt-2 max-h-40 rounded" />}
                    </div>
                  </div>
                </div>

                {filter === 'pending' && post.user_id && (
                  <div className="flex items-center gap-2 mt-3 flex-wrap justify-end">
                    <button onClick={() => handleDismiss(r.id)} disabled={actionId === r.id}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center gap-1 disabled:opacity-50"
                      data-testid={`dismiss-${r.id}`}>
                      <Check className="w-3.5 h-3.5" /> Dismiss
                    </button>
                    <button onClick={() => handleDeletePost(r.id)} disabled={actionId === r.id}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 flex items-center gap-1 disabled:opacity-50"
                      data-testid={`delete-post-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete Post
                    </button>
                    <button onClick={() => openSuspend(post.user_id, author.display_name || 'user')}
                      disabled={actionId === r.id}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 hover:bg-orange-100 flex items-center gap-1 disabled:opacity-50"
                      data-testid={`suspend-${r.id}`}>
                      <ShieldAlert className="w-3.5 h-3.5" /> Suspend User
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Suspend modal */}
      <AnimatePresence>
        {suspendModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSuspendModal(null)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-card rounded-arch-xl shadow-2xl max-w-md w-full"
              data-testid="suspend-modal">
              <div className="p-5 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 text-orange-600" />
                  <h3 className="font-heading text-lg text-text-main">Suspend @{suspendModal.displayName}</h3>
                </div>
                <button onClick={() => setSuspendModal(null)} className="p-1 hover:bg-primary/10 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-xs text-text-muted">Pick a duration. Suspended users can still read but cannot post or comment.</p>
                <div className="flex flex-wrap gap-2">
                  {DURATIONS.map(d => (
                    <button key={d.value} onClick={() => setDuration(d.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium ${duration === d.value ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-orange-50'}`}
                      data-testid={`suspend-duration-${d.value}`}>
                      {d.label}
                    </button>
                  ))}
                </div>
                <textarea value={suspendReason} onChange={e => setSuspendReason(e.target.value)}
                  placeholder="Internal reason (optional, max 300 chars)" rows={2} maxLength={300}
                  className="w-full px-3 py-2 rounded-arch border border-primary/20 bg-white dark:text-gray-800 text-sm" />
              </div>
              <div className="p-4 border-t border-border flex justify-end gap-2">
                <button onClick={() => setSuspendModal(null)} className="px-4 py-2 rounded-arch text-sm hover:bg-primary/5">Cancel</button>
                <button onClick={handleSuspend} disabled={actionId === suspendModal.userId}
                  className="px-4 py-2 rounded-arch text-sm font-medium bg-orange-600 text-white hover:bg-orange-700 flex items-center gap-2 disabled:opacity-50"
                  data-testid="suspend-confirm-btn">
                  {actionId === suspendModal.userId ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
                  Suspend
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReportsQueue;
